import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { apiClient } from '../apiClient'
import {
  assertTrainingWriteScope,
  invalidateTrainingModuleQueries,
  mapTrainingModuleWrite,
  TRAINING_QUERY_KEYS,
  trainingApiPrefix,
  trainingQueryKey,
  type OntologyTrainingModule,
  type TrainingApiOptions,
  type TrainingApiTarget,
  type TrainingModuleFormInput,
} from './trainingModuleApi'

type TrainingModuleCondition = {
  type: string
  values: string[]
}

type TrainingModule = {
  accuracyMax: number
  accuracyMin: number
  conditions: TrainingModuleCondition[]
  conditionsWeight: number
  dailyMaxAttempts: number
  dateRangeStart?: string
  dateRangeEnd?: string
  description: string
  engagementCount: number
  isPublished: boolean
  mediaId: string
  mediaLocation?: string
  movingDayCount?: number
  overallMaxAttempts: number
  stats: {
    avgAttemptsToPass: number
    trendAttemptsToPass: number
    avgAccuracy: number
    trendAccuracy: number
    avgTimeToClose: number
    trendTimeToClose: number
  }
  targetAccuracy: number
  targetResolution: number
  title: string
  trainingModuleId: string
  trainingOrder: number
  pipelineJobId?: string
  detailedFeedback: boolean
}

type EditModuleForm = TrainingModuleFormInput
type CreateModuleForm = TrainingModuleFormInput & { program_id: string; title: string; description: string }
type TrainingModuleResult<T extends TrainingApiTarget> = T extends 'legacy'
  ? TrainingModule
  : OntologyTrainingModule

type Annotation = {
  type: string
  label: string
  vertices: {
    x: number
    y: number
  }[]
  image_idx: number
  annotation_id: string
}

type ResolutionObject = {
  annotations: Annotation[]
  video_ids: string[]
  image_ids: string[]
  subrequests_ids: string[]
  time_to_close: number
  answers: string[]
  response: string
  engagement_id: string
}

type ResolutionResponse = {
  aggregatedScore: number
  trainingModuleResolutionId: string
}

export const PROGRAM_TRAINING_MODULE_KEY = TRAINING_QUERY_KEYS.configuration

export function useGetListByProgram<T extends TrainingApiTarget = 'legacy'>(
  programId?: string,
  options: TrainingApiOptions<T> = {}
) {
  const target = options.target ?? 'legacy'
  return useQuery({
    queryKey: trainingQueryKey(PROGRAM_TRAINING_MODULE_KEY, programId, target),
    queryFn: async () => {
      if (!programId) throw new Error('Training program is not available')
      const response = await apiClient.get<TrainingModuleResult<T>[]>(
        `${trainingApiPrefix(target)}/training-modules?programId=${encodeURIComponent(programId)}`
      )
      return response.data
    },
    enabled: Boolean(programId) && (target === 'legacy' || target === 'modern'),
  })
}

export function useGetByProgram(programId?: string, trainingModuleId?: string) {
  const trainingModules = useGetListByProgram(programId)

  return {
    ...trainingModules,
    data: trainingModules.data?.find((module) => module.trainingModuleId === trainingModuleId),
  }
}

export function useDelete(programId?: string, { target = 'legacy' }: TrainingApiOptions = {}) {
  const queryClient = useQueryClient()

  return useMutation({
    retry: target === 'modern' ? false : undefined,
    mutationFn: async (training_module_id: string) => {
      assertTrainingWriteScope(programId, target)
      const { data } = await apiClient.delete(
        `${trainingApiPrefix(target)}/training-modules/${training_module_id}`
      )
      return data
    },
    onSuccess: () => invalidateTrainingModuleQueries(queryClient, programId, target),
  })
}

export function useEdit(
  programId?: string,
  onSuccess?: () => void,
  onError?: () => void,
  { target = 'legacy' }: TrainingApiOptions = {}
) {
  const queryClient = useQueryClient()

  return useMutation({
    retry: target === 'modern' ? false : undefined,
    mutationFn: async ({
      values,
      trainingModuleId,
    }: {
      values: EditModuleForm
      trainingModuleId: string
    }) => {
      assertTrainingWriteScope(programId, target)
      if (target === 'legacy' && !values.conditions) values.conditions = []
      const { data } = await apiClient.patch(
        `${trainingApiPrefix(target)}/training-modules/${trainingModuleId}`,
        target === 'modern' ? mapTrainingModuleWrite(values) : values
      )
      return data
    },
    onSuccess: () => {
      invalidateTrainingModuleQueries(queryClient, programId, target)
      onSuccess?.()
    },
    onError,
  })
}

export function usePublish(programId?: string, { target = 'legacy' }: TrainingApiOptions = {}) {
  const queryClient = useQueryClient()

  return useMutation({
    retry: target === 'modern' ? false : undefined,
    mutationFn: async (values: { trainingModuleId: string; is_published?: boolean }) => {
      assertTrainingWriteScope(programId, target)
      const payload =
        target === 'modern' ? { isPublished: values.is_published } : { is_published: values.is_published }
      const { data } = await apiClient.patch(
        `${trainingApiPrefix(target)}/training-modules/${values.trainingModuleId}`,
        payload
      )
      return data
    },
    onSuccess: () => invalidateTrainingModuleQueries(queryClient, programId, target),
  })
}

export function useCreate(
  programId?: string,
  onSuccess?: () => void,
  onError?: () => void,
  { target = 'legacy' }: TrainingApiOptions = {}
) {
  const queryClient = useQueryClient()

  return useMutation({
    retry: target === 'modern' ? false : undefined,
    mutationFn: async (values: CreateModuleForm) => {
      assertTrainingWriteScope(programId, target)
      if (target === 'modern' && values.program_id !== programId)
        throw new Error('Training program does not match the current scope')
      if (target === 'legacy' && !values.conditions) values.conditions = []
      const payload =
        target === 'modern' ? { ...mapTrainingModuleWrite(values), programId: values.program_id } : values
      const { data } = await apiClient.post(`${trainingApiPrefix(target)}/training-modules`, payload)
      return data
    },
    onSuccess: () => {
      invalidateTrainingModuleQueries(queryClient, programId, target)
      onSuccess?.()
    },
    onError,
  })
}

export function useDuplicate(programId?: string, { target = 'legacy' }: TrainingApiOptions = {}) {
  const queryClient = useQueryClient()

  return useMutation({
    retry: target === 'modern' ? false : undefined,
    mutationFn: async (training_module_id: string) => {
      assertTrainingWriteScope(programId, target)
      const { data } = await apiClient.post(
        `${trainingApiPrefix(target)}/training-modules/${training_module_id}/duplicate`
      )
      return data
    },
    onSuccess: () => invalidateTrainingModuleQueries(queryClient, programId, target),
  })
}

export function usePostTrainingModuleResolution(trainingModuleId?: string, sessionId?: string) {
  return useMutation({
    mutationFn: async (resolution: ResolutionObject) => {
      const { data } = await apiClient.post<ResolutionResponse>(
        `/int/v2/training-modules/${trainingModuleId}/sessions/${sessionId}/resolutions`,
        { ...resolution }
      )
      return data
    },
  })
}
